import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Header from "../components/site/Header";
import Seo from "../components/site/Seo";
import Footer from "../components/site/Footer";
import CTAButton from "../components/site/CTAButton";
import Photo from "../components/site/Photo";
import {
  FAQSection, FinalCTA, Breadcrumb, SisterServices,
} from "../components/site/ServiceSections";
import { REGIONS, SUBURBS_BY_REGION, SUBURBS } from "../data/suburbs";

const FAQS = [
  { q: "Do you charge travel fees for outer-metro Melbourne suburbs?", a: "No — we cover all of Greater Melbourne metro without travel fees. From the CBD to Werribee, Point Cook, Pakenham and Frankston, it's the same shoot day rate. Regional Victoria (Geelong, Mornington Peninsula, Bendigo) is quoted with a small uplift." },
  { q: "How quickly can you get to my suburb?", a: "Most Melbourne metro shoots are booked 7–14 days ahead. Urgent (48–72 hour) turnarounds are possible for any suburb — just mention the deadline on the call." },
  { q: "Why are some suburbs not listed?", a: "These are the 23 we shoot most frequently. We cover every Greater Melbourne suburb — if yours isn't here, call directly and we'll confirm the same day." },
];

export default function LocationsHub() {
  return (
    <div className="min-h-screen bg-ink text-bone">
      <Seo />
      <Header />

      {/* Hero */}
      <section className="relative min-h-[70svh] overflow-hidden bg-ink">
        <div className="absolute inset-0">
          <Photo slug="hero-spread-01" alt="Restaurant photographer Melbourne — coverage map" priority ratio="auto" className="!relative h-full w-full" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/45 to-ink" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/30 to-transparent" />

        <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 min-h-[70svh] flex flex-col pt-24 sm:pt-28 lg:pt-32 pb-10 lg:pb-12">
          <Breadcrumb current="Melbourne Locations" />
          <div className="flex-1 flex flex-col justify-center max-w-5xl">
            <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 sm:mb-7 font-bold">
              Coverage · 23 suburbs · One photographer
            </p>
            <h1 className="font-display tracking-editorial leading-[0.92]" style={{ fontSize: "clamp(2.5rem, 7vw, 6rem)" }}>
              Restaurant photography
              <br />
              across <span className="italic font-light text-ember">Melbourne.</span>
            </h1>
            <p className="mt-6 sm:mt-8 max-w-2xl text-base sm:text-lg md:text-xl leading-relaxed text-bone/85">
              We shoot every week in the suburbs below — from CBD fine dining to Werribee delivery menus and Brunswick brunch. No travel fees inside metro Melbourne. Pick your suburb to see local context.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#regions"><CTAButton variant="primary" size="lg">Browse Suburbs</CTAButton></a>
              <a href="tel:0494756111"><CTAButton variant="ghost" size="lg" arrow={false}>Call 0494 756 111</CTAButton></a>
            </div>
          </div>
        </div>
      </section>

      {/* Regions */}
      <section id="regions" className="bg-bone text-ink py-20 lg:py-28">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-8 lg:gap-16 mb-14 items-end">
            <div className="col-span-12 lg:col-span-7">
              <p className="text-xs uppercase tracking-wider2 text-ember mb-5 font-bold">Coverage map · 4 regions</p>
              <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2rem, 5vw, 4rem)" }}>
                Pick your suburb.
                <br /><span className="italic font-light text-ember">We&apos;ll shoot it.</span>
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-5 lg:pb-3">
              <p className="text-char text-base md:text-lg leading-relaxed max-w-md">
                The 23 suburbs we shoot most frequently. Each page covers the local dining scene, what photography typically gets briefed there, and indicative pricing.
              </p>
            </div>
          </div>

          <div className="space-y-16">
            {SUBURBS_BY_REGION.map((r) => (
              <div key={r.id} data-testid={`region-${r.id}`}>
                <div className="flex items-baseline justify-between gap-6 mb-6 border-b border-ink/15 pb-4">
                  <h3 className="font-display tracking-editorial leading-tight" style={{ fontSize: "clamp(1.5rem, 3vw, 2.5rem)" }}>
                    {r.label}
                  </h3>
                  <p className="text-xs uppercase tracking-wider2 font-bold text-ember whitespace-nowrap">
                    {r.suburbs.length} suburbs
                  </p>
                </div>
                <p className="text-char-mute text-base md:text-lg leading-relaxed mb-8 max-w-3xl">{r.blurb}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10 border border-ink/10">
                  {r.suburbs.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/restaurant-photographer/${s.slug}`}
                      data-testid={`suburb-${s.slug}`}
                      className="group bg-bone hover:bg-ink hover:text-bone transition-colors p-6 flex flex-col"
                    >
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <p className="font-display text-xl md:text-2xl tracking-editorial leading-tight group-hover:text-ember transition-colors">
                          {s.name}
                        </p>
                        <ArrowUpRight size={16} strokeWidth={1.6} className="opacity-40 group-hover:text-ember group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0 mt-1" />
                      </div>
                      <p className="text-sm text-char-mute group-hover:text-bone/65 transition-colors line-clamp-2 leading-relaxed">
                        {s.dining[0]}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SisterServices keys={["menu", "ubereats", "cafe", "gbp"]} />
      <FAQSection
        eyebrow="Common questions · Coverage"
        headline="Coverage, travel,"
        italic="and suburb-level briefs."
        intro="If your suburb isn't listed, call directly — we cover all of Greater Melbourne."
        faqs={FAQS}
      />
      <FinalCTA
        eyebrow="Final step · Hand-confirmed quotes"
        headline="Ready to lock"
        italic="your shoot day?"
        intro="One call, one quote, one shoot day. Most Melbourne metro briefs confirmed in under five minutes."
        backdropSlug="hero-banquet-01"
      />
      <Footer />
    </div>
  );
}

export { REGIONS, SUBURBS };
